/**
 * Key Storage Module
 *
 * Stores encrypted session keypairs in IndexedDB.
 * The encryption key is derived from either:
 * - PRF (passkey) - biometric unlock
 * - PIN - manual entry
 *
 * Storage is isolated to signer.lumenbro.com origin.
 */

import * as idb from 'idb-keyval';
import type { P256PublicKey, P256Keypair } from './p256-keygen';

// IndexedDB key for the stored keypair
const STORAGE_KEY = 'session_keypair';

export type EncryptionMethod = 'prf' | 'pin';

export interface StoredKeypair {
  // Encrypted private key (AES-GCM)
  encryptedPrivateKey: ArrayBuffer;

  // Public key coordinates (not encrypted - needed for display/revocation)
  publicKey: P256PublicKey;

  // AES-GCM IV (random per encryption)
  iv: Uint8Array;

  // PBKDF2 salt (only for PIN mode)
  salt?: Uint8Array;

  // Passkey credential ID (only for PRF mode)
  credentialId?: string;

  // Which encryption method was used
  encryptionMethod: EncryptionMethod;

  // Metadata
  createdAt: number;
  lastUsedAt?: number;
}

/**
 * Store an encrypted session keypair.
 *
 * @param keypair - The P-256 keypair to store
 * @param encryptionKey - AES-GCM key derived from PRF or PIN
 * @param method - Which encryption method was used
 * @param credentialId - Passkey credential ID (for PRF mode)
 * @param salt - PBKDF2 salt (for PIN mode)
 */
export async function storeSessionKeypair(
  keypair: P256Keypair,
  encryptionKey: CryptoKey,
  method: EncryptionMethod,
  credentialId?: string,
  salt?: Uint8Array
): Promise<void> {
  // Generate random IV for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt the private key
  const encryptedPrivateKey = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    keypair.privateKey
  );

  const stored: StoredKeypair = {
    encryptedPrivateKey,
    publicKey: keypair.publicKey,
    iv,
    encryptionMethod: method,
    createdAt: Date.now(),
    ...(method === 'prf' ? { credentialId } : { salt }),
  };

  await idb.set(STORAGE_KEY, stored);
  console.log('[KeyStorage] Session keypair stored (method:', method + ')');
}

/**
 * Load and decrypt the session keypair.
 *
 * @param encryptionKey - AES-GCM key derived from PRF or PIN
 * @returns The decrypted P-256 keypair
 * @throws If no keypair stored or decryption fails
 */
export async function loadSessionKeypair(encryptionKey: CryptoKey): Promise<P256Keypair> {
  const stored = await idb.get<StoredKeypair>(STORAGE_KEY);

  if (!stored) {
    throw new Error('No session keypair stored');
  }

  // Decrypt the private key
  try {
    const privateKeyBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: stored.iv },
      encryptionKey,
      stored.encryptedPrivateKey
    );

    // Update last used timestamp
    stored.lastUsedAt = Date.now();
    await idb.set(STORAGE_KEY, stored);

    return {
      privateKey: new Uint8Array(privateKeyBuffer),
      publicKey: stored.publicKey,
    };
  } catch (error) {
    // Decryption failed - likely wrong key (wrong PIN or changed passkey)
    throw new Error('Failed to decrypt keypair - check your PIN or passkey');
  }
}

/**
 * Get the stored keypair metadata (without decrypting).
 *
 * @returns Stored keypair metadata, or null if none
 */
export async function getStoredKeypairInfo(): Promise<{
  publicKey: P256PublicKey;
  encryptionMethod: EncryptionMethod;
  credentialId?: string;
  createdAt: number;
  lastUsedAt?: number;
} | null> {
  const stored = await idb.get<StoredKeypair>(STORAGE_KEY);

  if (!stored) {
    return null;
  }

  return {
    publicKey: stored.publicKey,
    encryptionMethod: stored.encryptionMethod,
    credentialId: stored.credentialId,
    createdAt: stored.createdAt,
    lastUsedAt: stored.lastUsedAt,
  };
}

/**
 * Check if a session keypair is stored.
 */
export async function hasStoredKeypair(): Promise<boolean> {
  const stored = await idb.get<StoredKeypair>(STORAGE_KEY);
  return stored !== undefined;
}

/**
 * Get the encryption method and credential ID for the stored keypair.
 * Used to determine how to unlock the keypair.
 */
export async function getUnlockRequirements(): Promise<{
  method: EncryptionMethod;
  credentialId?: string;
  salt?: Uint8Array;
} | null> {
  const stored = await idb.get<StoredKeypair>(STORAGE_KEY);

  if (!stored) {
    return null;
  }

  return {
    method: stored.encryptionMethod,
    credentialId: stored.credentialId,
    salt: stored.salt,
  };
}

/**
 * Delete the stored session keypair.
 * Call this when revoking the signer.
 */
export async function deleteStoredKeypair(): Promise<void> {
  await idb.del(STORAGE_KEY);
  console.log('[KeyStorage] Session keypair deleted');
}

/**
 * Get the public key of the stored keypair (without decrypting).
 * Returns null if no keypair stored.
 */
export async function getStoredPublicKey(): Promise<P256PublicKey | null> {
  const stored = await idb.get<StoredKeypair>(STORAGE_KEY);
  return stored?.publicKey ?? null;
}
